import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  buildDefaultFinancialAidCycleForYear,
  getFinancialAidCycleForYear,
  listFinancialAidCycles,
  resolveCurrentFinancialAidSchoolYear,
  resolveFinancialAidRequirementState,
  type FinancialAidRequirementPolicy,
  type FinancialAidRequirementState,
  type FinancialAidCycle,
  type FinancialAidSubmissionRequirement,
  type SettingsState,
  upsertFinancialAidCycle,
} from '@triedge/staff-settings-domain';
import { Card, SectionHeader, Input, Button, Icon, CustomSelect, Toggle } from '../components/ui';
import { DropzoneField } from '../components/DropzoneField';
import { SectionDivider } from '../components/SectionDivider';
import {
  useGetFinancialAidHistoricalImportStatusQuery,
  useLazyGetFinancialAidHistoricalImportTemplateQuery,
  useRequestFinancialAidHistoricalImportUploadMutation,
  useStartFinancialAidHistoricalImportMutation,
} from '../../../state/domainRouterApi';
import { EngineeringSupportBanner, SupportErrorMessage } from '../../../components/overlays/EngineeringSupportBanner';
import { useStaffSettingsState } from '../hooks/useStaffSettingsState';
import { FINANCIAL_AID_REQUIREMENT_FIELDS } from '../../../financial-aid/financialAidRequirementFields';
import {
  PARENT_FINANCIAL_AID_ATTESTATION_KEYS,
  PARENT_FINANCIAL_AID_DEFAULT_ATTESTATION_TEXT,
  sanitizeFinancialAidCertificationStatements,
} from '../../../financial-aid/financialAidCertificationStatements';

const SETTINGS_MODULE_KEY = 'staff_settings';
const SETTINGS_DEFAULT_ORG_ID = '00000000-0000-4000-8000-000000000000';
const MAX_IMPORT_SIZE_BYTES = 25 * 1024 * 1024;
const IMPORT_POLL_TIMEOUT_MS = 45000;
const IMPORT_POLL_INTERVAL_MS = 750;
const TERMINAL_IMPORT_STATUSES = new Set(['completed', 'failed']);

type FinancialAidDocs = {
  w2: boolean;
  federalTaxReturn: boolean;
  stateTaxReturn: boolean;
  payStubs: boolean;
  bankStatements: boolean;
  investmentStatements: boolean;
  businessTaxReturn: boolean;
  proofOfBenefits: boolean;
  mortgageStatement: boolean;
  otherIncomeProof: boolean;
  other: boolean;
};

type FinancialAidNotifications = {
  appSubmitted: boolean;
  statusChange: boolean;
  infoNeeded: boolean;
  decisionReady: boolean;
  appealReceived: boolean;
  newApp: boolean;
  resubmission: boolean;
  appealSubmitted: boolean;
  overdue: boolean;
};

type FinancialAidCycleFormState = {
  schoolYear: string;
  openDate: string;
  onTimeDeadline: string;
  hardCutoffDeadline: string;
  lateFeeAmount: string;
  submissionRequirement: FinancialAidSubmissionRequirement;
};

type FinancialAidVisibleHydrationState = {
  cycle: FinancialAidCycleFormState;
  budget: {
    annualBudget: string;
    warningThreshold: string;
    criticalThreshold: string;
  };
  docs: FinancialAidDocs;
  notifs: FinancialAidNotifications;
  reminderFrequency: 'daily' | 'every_2_days' | 'weekly';
  requirementPolicy: FinancialAidRequirementPolicy;
  certificationStatements: Record<string, string>;
};

const FINANCIAL_AID_SUBMISSION_REQUIREMENT_OPTIONS: Array<{
  label: string;
  value: FinancialAidSubmissionRequirement;
}> = [
  { label: 'Allow submission without registration fee', value: 'open_cycle' },
  { label: 'Require enrollment registration fee', value: 'registration_fee_paid' },
];

const DOC_DEFINITIONS = [
  { key: 'w2', label: 'W-2 Forms', id: 'w2_forms', defaultEnabled: true },
  {
    key: 'federalTaxReturn',
    label: 'Federal Tax Return',
    id: 'tax_return_federal',
    defaultEnabled: true,
    legacyIds: ['federal_tax_form_1040_2024', 'federal_tax_form_1040_2025'],
  },
  {
    key: 'stateTaxReturn',
    label: 'State Tax Return',
    id: 'tax_return_state',
    defaultEnabled: true,
    legacyIds: ['state_tax_forms_2024', 'state_tax_forms_2025'],
  },
  { key: 'payStubs', label: 'Recent Pay Stubs', id: 'pay_stubs', defaultEnabled: true },
  { key: 'bankStatements', label: 'Bank Statements', id: 'bank_statements', defaultEnabled: true },
  { key: 'investmentStatements', label: 'Investment Statements', id: 'investment_statements', defaultEnabled: true },
  {
    key: 'businessTaxReturn',
    label: 'Business Tax Return',
    id: 'business_tax_return',
    defaultEnabled: true,
    legacyIds: ['form_1065', 'form_1120s'],
  },
  { key: 'proofOfBenefits', label: 'Proof of Benefits', id: 'proof_of_benefits', defaultEnabled: true },
  { key: 'mortgageStatement', label: 'Mortgage Statement', id: 'mortgage_statement', defaultEnabled: true },
  {
    key: 'otherIncomeProof',
    label: 'Other Income Proof',
    id: 'other_income_proof',
    defaultEnabled: false,
    legacyIds: ['form_4506', 'other_scholarship_applications', 'other_scholarship_award_letters'],
  },
  {
    key: 'other',
    label: 'Other',
    id: 'other',
    defaultEnabled: false,
    // Catch-all slot for letters or extra material; parents may leave it empty.
    optionalUpload: true,
  },
] as const;

const FORM_REQUIREMENT_FIELDS = FINANCIAL_AID_REQUIREMENT_FIELDS.filter((field) => field.togglable);

const FORM_REQUIREMENT_FIELD_GROUPS = FORM_REQUIREMENT_FIELDS.reduce<
  Array<{ group: string; fields: typeof FORM_REQUIREMENT_FIELDS }>
>((groups, field) => {
  const group = groups.find((entry) => entry.group === field.group);
  if (group) {
    group.fields = [...group.fields, field];
    return groups;
  }
  return [...groups, { group: field.group, fields: [field] }];
}, []);

const FORM_REQUIREMENT_FIELD_KEYS = new Set(FORM_REQUIREMENT_FIELDS.map((field) => field.fieldKey));
const FORM_REQUIREMENT_MODE_OPTIONS = [
  { label: 'Hidden', value: 'hidden' },
  { label: 'Optional', value: 'optional' },
  { label: 'Required', value: 'required' },
] as const;

const toRequirementModeTestId = (fieldKey: string): string =>
  `settings-financial-aid-requirement-${fieldKey.replace(/[^a-zA-Z0-9]+/g, '-')}-mode`;

const sanitizeRequirementPolicy = (
  policy: FinancialAidRequirementPolicy,
): FinancialAidRequirementPolicy => ({
  fieldRequirements: Object.fromEntries(
    Object.entries(policy.fieldRequirements).filter(([fieldKey, state]) =>
      FORM_REQUIREMENT_FIELD_KEYS.has(fieldKey) && (state === 'hidden' || state === 'required' || state === 'optional')
    ),
  ),
});

const updateRequirementPolicy = (
  current: FinancialAidRequirementPolicy,
  fieldKey: string,
  mode: FinancialAidRequirementState,
): FinancialAidRequirementPolicy => ({
  fieldRequirements: {
    ...sanitizeRequirementPolicy(current).fieldRequirements,
    [fieldKey]: mode,
  },
});

const readErrorMessage = (error: unknown, fallback: string): string => {
  if (!error || typeof error !== 'object') {
    return fallback;
  }
  const message = (error as { data?: { message?: unknown }; message?: unknown }).data?.message
    ?? (error as { message?: unknown }).message;
  if (typeof message !== 'string') {
    return fallback;
  }
  const normalized = message.trim();
  return normalized.length > 0 ? normalized : fallback;
};

const asRecord = (value: unknown): Record<string, unknown> => (
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
);

// ─── Import Phase State Machine ───────────────────────────────────────────────
//
// Replaces ad-hoc isUploadingImport/importMessage/importError boolean+string state
// with a discriminated union that makes invalid states unrepresentable.
// fullValue is never stored in RTK cache — only in component-local state.
//
// ─────────────────────────────────────────────────────────────────────────────

type ImportPhase =
  | { phase: 'idle' }
  | { phase: 'file_selected'; file: File; sizeBytes: number }
  | { phase: 'uploading'; progress?: number }
  | { phase: 'processing'; importId: string }
  | { phase: 'complete'; accepted: number; rejected: number }
  | { phase: 'partial-success'; accepted: number; rejected: number; firstErrors: string[] }
  | { phase: 'failed'; error: string };

type ImportStatusSnapshot = {
  status: string;
  acceptedRows: number;
  rejectedRows: number;
  errorSummary: string;
  firstErrors: string[];
};

const toImportStatusSnapshot = (value: unknown): ImportStatusSnapshot => {
  const record = asRecord(value);
  const rawErrors = Array.isArray(record.errors) ? record.errors : [];
  const errorSummary = String(record.errorSummary ?? '').trim();
  const firstErrors =
    rawErrors.length > 0
      ? rawErrors.slice(0, 5).map((e) => String(e ?? '').trim()).filter(Boolean)
      : errorSummary
      ? errorSummary.split('\n').slice(0, 5).map((e) => e.trim()).filter(Boolean)
      : [];
  return {
    status: String(record.status ?? '').trim().toLowerCase(),
    acceptedRows: Number(record.acceptedRows ?? 0),
    rejectedRows: Number(record.rejectedRows ?? 0),
    errorSummary,
    firstErrors,
  };
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const isCsv = (file: File): boolean => file.name.toLowerCase().endsWith('.csv');

const resolveCsvMimeType = (file: File): string => {
  const fileType = String(file.type || '').trim().toLowerCase();
  if (fileType.includes('csv') || fileType === 'application/vnd.ms-excel') {
    return fileType;
  }
  return 'text/csv';
};

const formatImportStatus = (value: unknown): string => {
  const status = String(value || '').trim().toLowerCase();
  if (!status) return 'Idle';
  return status
    .split('_')
    .map((token) => token.slice(0, 1).toUpperCase() + token.slice(1))
    .join(' ');
};

const toNumber = (value: string): number => {
  const numeric = Number(String(value).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(numeric) ? numeric : 0;
};

const toCycleFormState = (
  cycle: FinancialAidCycle | null | undefined,
  fallbackSchoolYear = '',
): FinancialAidCycleFormState => ({
  schoolYear: String(cycle?.schoolYear || fallbackSchoolYear || '').trim(),
  openDate: String(cycle?.openDate || ''),
  onTimeDeadline: String(cycle?.onTimeDeadline || ''),
  hardCutoffDeadline: String(cycle?.hardCutoffDeadline || ''),
  lateFeeAmount: String(cycle?.lateFeeAmount ?? 0),
  submissionRequirement: cycle?.submissionRequirement ?? 'open_cycle',
});

const stableStringify = (value: unknown): string => {
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return `{${Object.keys(record)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableStringify(record[key])}`)
      .join(',')}}`;
  }
  const serialized = JSON.stringify(value);
  return serialized === undefined ? 'undefined' : serialized;
};

const buildFinancialAidHydrationSignature = (state: SettingsState): string =>
  stableStringify(state.financialAid);

const parseSchoolYearStart = (value: unknown): number | null => {
  const match = String(value ?? '').trim().match(/^(\d{4})\s*[-/]/);
  const year = Number(match?.[1]);
  return Number.isFinite(year) && year > 0 ? year : null;
};

const buildSchoolYearLabel = (startYear: number): string => `${startYear}-${startYear + 1}`;

const resolveDefaultFinancialAidSchoolYear = (financialAid: SettingsState['financialAid']): string => {
  const selectedSchoolYear = String(financialAid.cycle.schoolYear ?? '').trim();
  const selectedCycle = selectedSchoolYear
    ? getFinancialAidCycleForYear(financialAid, selectedSchoolYear)
    : null;
  if (selectedCycle?.schoolYear) return selectedCycle.schoolYear;

  const currentFinancialAidYear = resolveCurrentFinancialAidSchoolYear();
  const currentCycle = getFinancialAidCycleForYear(financialAid, currentFinancialAidYear);
  if (currentCycle?.schoolYear) return currentCycle.schoolYear;

  return currentFinancialAidYear;
};

const buildFinancialAidDocState = (requiredDocuments: unknown): FinancialAidDocs => {
  const rows = Array.isArray(requiredDocuments) ? requiredDocuments : null;
  const emptyState = DOC_DEFINITIONS.reduce((accumulator, definition) => {
    accumulator[definition.key] = false;
    return accumulator;
  }, {} as FinancialAidDocs);
  if (rows?.length === 0) {
    return emptyState;
  }
  const lookup = new Map<string, boolean>();
  rows?.forEach((row) => {
    const record = row && typeof row === 'object' && !Array.isArray(row)
      ? (row as Record<string, unknown>)
      : {};
    const id = String(record.id ?? '').trim();
    if (!id) return;
    const enabled = typeof record.enabled === 'boolean' ? record.enabled : undefined;
    if (enabled !== undefined) lookup.set(id, enabled);
  });
  const readDocEnabled = (definition: typeof DOC_DEFINITIONS[number]): boolean => {
    const canonicalEnabled = lookup.get(definition.id);
    if (canonicalEnabled !== undefined) {
      return canonicalEnabled;
    }
    const legacyValues = ('legacyIds' in definition ? definition.legacyIds : [])
      .map((id) => lookup.get(id))
      .filter((enabled): enabled is boolean => enabled !== undefined);
    if (legacyValues.length > 0) {
      return legacyValues.some(Boolean);
    }
    return definition.defaultEnabled;
  };
  return {
    w2: readDocEnabled(DOC_DEFINITIONS[0]),
    federalTaxReturn: readDocEnabled(DOC_DEFINITIONS[1]),
    stateTaxReturn: readDocEnabled(DOC_DEFINITIONS[2]),
    payStubs: readDocEnabled(DOC_DEFINITIONS[3]),
    bankStatements: readDocEnabled(DOC_DEFINITIONS[4]),
    investmentStatements: readDocEnabled(DOC_DEFINITIONS[5]),
    businessTaxReturn: readDocEnabled(DOC_DEFINITIONS[6]),
    proofOfBenefits: readDocEnabled(DOC_DEFINITIONS[7]),
    mortgageStatement: readDocEnabled(DOC_DEFINITIONS[8]),
    otherIncomeProof: readDocEnabled(DOC_DEFINITIONS[9]),
    other: readDocEnabled(DOC_DEFINITIONS[10]),
  };
};

const toFinancialAidVisibleHydrationState = (
  state: SettingsState,
  selectedSchoolYear: string | null,
): FinancialAidVisibleHydrationState => {
  const financialAid = state.financialAid;
  const resolvedSchoolYear = selectedSchoolYear || resolveDefaultFinancialAidSchoolYear(financialAid);
  const selectedCycle =
    getFinancialAidCycleForYear(financialAid, resolvedSchoolYear)
    ?? buildDefaultFinancialAidCycleForYear(financialAid, resolvedSchoolYear);
  return {
    cycle: toCycleFormState(selectedCycle, resolvedSchoolYear),
    budget: {
      annualBudget: String(financialAid.budget.annualBudget),
      warningThreshold: String(financialAid.budget.warningThreshold),
      criticalThreshold: String(financialAid.budget.criticalThreshold),
    },
    docs: buildFinancialAidDocState(financialAid.requiredDocuments),
    certificationStatements: sanitizeFinancialAidCertificationStatements(
      financialAid.certificationStatements,
    ),
    notifs: {
      appSubmitted: financialAid.notifications.parent.applicationSubmitted,
      statusChange: financialAid.notifications.parent.statusChange,
      infoNeeded: financialAid.notifications.parent.moreInfoNeeded,
      decisionReady: financialAid.notifications.parent.decisionReady,
      appealReceived: financialAid.notifications.parent.appealReceived,
      newApp: financialAid.notifications.staff.newApplication,
      resubmission: financialAid.notifications.staff.resubmission,
      appealSubmitted: financialAid.notifications.staff.appealSubmitted,
      overdue: financialAid.notifications.staff.overdueReminder,
    },
    reminderFrequency: financialAid.notifications.overdueReminderFrequency,
    requirementPolicy: sanitizeRequirementPolicy(financialAid.requirementPolicy),
  };
};

const buildFinancialAidVisibleHydrationSignature = (
  state: SettingsState,
  selectedSchoolYear: string | null,
): string => stableStringify(toFinancialAidVisibleHydrationState(state, selectedSchoolYear));

export default function FinancialAid() {
  const {
    settingsState,
    persistPatch,
    isLoading,
    isSaving,
    saveError,
    clearSaveError,
    lastSavedAt,
  } = useStaffSettingsState();
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const [importPhase, setImportPhase] = useState<ImportPhase>({ phase: 'idle' });
  const uploadInFlightRef = useRef(false);
  const selectedCycleYearRef = useRef<string | null>(null);
  const lastHydratedFinancialAidSignatureRef = useRef<string | null>(null);
  const lastHydratedVisibleSignatureRef = useRef<string | null>(null);
  const deferredFinancialAidSettingsStateRef = useRef<SettingsState | null>(null);
  const previousVisibleHydrationSignatureRef = useRef<string | null>(null);
  const wasSavingRef = useRef(false);
  const saveHadVisibleEditRef = useRef(false);
  const skipNextHydrationAfterSaveErrorRef = useRef(false);
  const hasUnsavedLocalEditRef = useRef(false);
  const lastObservedSavedAtRef = useRef<string | null>(null);
  const pendingLocalSaveRevisionRef = useRef(0);
  const consumedLocalSaveRevisionRef = useRef(0);

  const [cycle, setCycle] = useState<FinancialAidCycleFormState>({
    schoolYear: '',
    openDate: '',
    onTimeDeadline: '',
    hardCutoffDeadline: '',
    lateFeeAmount: '0',
    submissionRequirement: 'open_cycle',
  });
  const [budget, setBudget] = useState({
    annualBudget: '0',
    warningThreshold: '80',
    criticalThreshold: '95',
  });
  const [docs, setDocs] = useState<FinancialAidDocs>({
    w2: true,
    federalTaxReturn: true,
    stateTaxReturn: true,
    payStubs: true,
    bankStatements: true,
    investmentStatements: true,
    businessTaxReturn: true,
    proofOfBenefits: true,
    mortgageStatement: true,
    otherIncomeProof: false,
    other: false,
  });
  const [notifs, setNotifs] = useState<FinancialAidNotifications>({
    appSubmitted: true,
    statusChange: true,
    infoNeeded: true,
    decisionReady: true,
    appealReceived: true,
    newApp: true,
    resubmission: true,
    appealSubmitted: true,
    overdue: true,
  });
  const [reminderFrequency, setReminderFrequency] = useState<'daily' | 'every_2_days' | 'weekly'>('daily');
  const [requirementPolicy, setRequirementPolicy] = useState<FinancialAidRequirementPolicy>(
    settingsState.financialAid.requirementPolicy,
  );
  const [certificationStatements, setCertificationStatements] = useState<Record<string, string>>(
    () => sanitizeFinancialAidCertificationStatements(
      settingsState.financialAid.certificationStatements,
    ),
  );

  const currentVisibleHydrationSignature = useMemo(() => stableStringify({
    cycle,
    budget,
    docs,
    certificationStatements: sanitizeFinancialAidCertificationStatements(certificationStatements),
    notifs,
    reminderFrequency,
    requirementPolicy: sanitizeRequirementPolicy(requirementPolicy),
  }), [budget, certificationStatements, cycle, docs, notifs, reminderFrequency, requirementPolicy]);

  const [fetchTemplate] = useLazyGetFinancialAidHistoricalImportTemplateQuery();
  const { data: importStatus, refetch: refetchImportStatus } = useGetFinancialAidHistoricalImportStatusQuery({
    orgId: SETTINGS_DEFAULT_ORG_ID,
    moduleKey: SETTINGS_MODULE_KEY,
    importId: 'latest',
  });
  const [requestImportUpload] = useRequestFinancialAidHistoricalImportUploadMutation();
  const [startHistoricalImport] = useStartFinancialAidHistoricalImportMutation();

  const cycleOptions = useMemo(() => {
    const years = new Set<string>();
    listFinancialAidCycles(settingsState.financialAid).forEach((entry) => {
      if (entry.schoolYear) years.add(entry.schoolYear);
    });
    if (settingsState.financialAid.cycle.schoolYear) {
      years.add(settingsState.financialAid.cycle.schoolYear);
    }
    if (settingsState.academicYear.label) {
      years.add(settingsState.academicYear.label);
    }

    const currentFinancialAidYear = resolveCurrentFinancialAidSchoolYear();
    years.add(currentFinancialAidYear);

    const anchorYear =
      parseSchoolYearStart(currentFinancialAidYear)
      ?? parseSchoolYearStart(settingsState.financialAid.cycle.schoolYear)
      ?? parseSchoolYearStart(settingsState.academicYear.label)
      ?? parseSchoolYearStart(settingsState.academicYear.startDate)
      ?? new Date().getFullYear();
    for (let offset = -1; offset <= 1; offset += 1) {
      years.add(buildSchoolYearLabel(anchorYear + offset));
    }

    return [...years]
      .filter(Boolean)
      .sort((left, right) => left.localeCompare(right))
      .map((year) => ({ label: year, value: year }));
  }, [settingsState]);

  const pollImportUntilTerminal = async (): Promise<ImportStatusSnapshot> => {
    const deadline = Date.now() + IMPORT_POLL_TIMEOUT_MS;
    while (Date.now() < deadline) {
      const refetchResult = await refetchImportStatus();
      const refetchRecord = asRecord(refetchResult);
      const payload = refetchRecord.data ?? refetchResult;
      const snapshot = toImportStatusSnapshot(payload);
      if (TERMINAL_IMPORT_STATUSES.has(snapshot.status)) {
        return snapshot;
      }
      await sleep(IMPORT_POLL_INTERVAL_MS);
    }
    throw new Error('Historical import timed out while waiting for completion.');
  };

  useLayoutEffect(() => {
    // Track whether the user edited visible form state during an in-flight save.
    if (isSaving && !wasSavingRef.current) {
      skipNextHydrationAfterSaveErrorRef.current = false;
      saveHadVisibleEditRef.current = false;
      wasSavingRef.current = true;
      pendingLocalSaveRevisionRef.current += 1;
      previousVisibleHydrationSignatureRef.current = currentVisibleHydrationSignature;
      return;
    }
    if (
      isSaving
      && previousVisibleHydrationSignatureRef.current !== null
      && currentVisibleHydrationSignature !== previousVisibleHydrationSignatureRef.current
    ) {
      saveHadVisibleEditRef.current = true;
    }
    if (lastHydratedVisibleSignatureRef.current !== null) {
      hasUnsavedLocalEditRef.current =
        currentVisibleHydrationSignature !== lastHydratedVisibleSignatureRef.current;
    }
    if (!isSaving && wasSavingRef.current) {
      wasSavingRef.current = false;
    }
    previousVisibleHydrationSignatureRef.current = currentVisibleHydrationSignature;
  }, [currentVisibleHydrationSignature, isSaving]);

  useLayoutEffect(() => {
    if (isLoading) {
      return;
    }
    // Hydrate from backend settings only when it cannot overwrite a newer local edit.
    const hydrateFromSettingsState = (
      nextSettingsState: SettingsState,
      nextSignature = buildFinancialAidHydrationSignature(nextSettingsState),
    ) => {
      const visibleState = toFinancialAidVisibleHydrationState(
        nextSettingsState,
        selectedCycleYearRef.current,
      );
      const visibleSignature = stableStringify(visibleState);
      setCycle(visibleState.cycle);
      setBudget(visibleState.budget);
      setDocs(visibleState.docs);
      setCertificationStatements(visibleState.certificationStatements);
      setNotifs(visibleState.notifs);
      setReminderFrequency(visibleState.reminderFrequency);
      setRequirementPolicy(visibleState.requirementPolicy);
      lastHydratedFinancialAidSignatureRef.current = nextSignature;
      lastHydratedVisibleSignatureRef.current = visibleSignature;
      previousVisibleHydrationSignatureRef.current = visibleSignature;
      hasUnsavedLocalEditRef.current = false;
      lastObservedSavedAtRef.current = lastSavedAt;
      consumedLocalSaveRevisionRef.current = pendingLocalSaveRevisionRef.current;
      saveHadVisibleEditRef.current = false;
    };

    const currentSignature = buildFinancialAidHydrationSignature(settingsState);
    if (isSaving) {
      skipNextHydrationAfterSaveErrorRef.current = false;
      if (currentSignature !== lastHydratedFinancialAidSignatureRef.current) {
        deferredFinancialAidSettingsStateRef.current = settingsState;
      }
      return;
    }

    if (saveError) {
      skipNextHydrationAfterSaveErrorRef.current = true;
      deferredFinancialAidSettingsStateRef.current = null;
      consumedLocalSaveRevisionRef.current = pendingLocalSaveRevisionRef.current;
      lastObservedSavedAtRef.current = lastSavedAt;
      return;
    }

    if (skipNextHydrationAfterSaveErrorRef.current) {
      skipNextHydrationAfterSaveErrorRef.current = false;
      deferredFinancialAidSettingsStateRef.current = null;
      const currentServerVisibleSignature = buildFinancialAidVisibleHydrationSignature(
        settingsState,
        selectedCycleYearRef.current,
      );
      if (currentServerVisibleSignature !== currentVisibleHydrationSignature) {
        lastHydratedFinancialAidSignatureRef.current = currentSignature;
        previousVisibleHydrationSignatureRef.current = currentVisibleHydrationSignature;
        hasUnsavedLocalEditRef.current = true;
        return;
      }
    }

    let candidateSettingsState = settingsState;
    let candidateSignature = currentSignature;
    const deferredSettingsState = deferredFinancialAidSettingsStateRef.current;
    if (deferredSettingsState) {
      const deferredSignature = buildFinancialAidHydrationSignature(deferredSettingsState);
      deferredFinancialAidSettingsStateRef.current = null;
      if (currentSignature === deferredSignature) {
        candidateSettingsState = deferredSettingsState;
        candidateSignature = deferredSignature;
      }
    }

    if (candidateSignature === lastHydratedFinancialAidSignatureRef.current) {
      return;
    }

    const candidateVisibleSignature = buildFinancialAidVisibleHydrationSignature(
      candidateSettingsState,
      selectedCycleYearRef.current,
    );
    const hasVisibleLocalDivergence =
      hasUnsavedLocalEditRef.current
      || (
        lastHydratedVisibleSignatureRef.current !== null
        && currentVisibleHydrationSignature !== lastHydratedVisibleSignatureRef.current
      );
    const hasOwnSaveConfirmation =
      Boolean(lastSavedAt)
      && lastSavedAt !== lastObservedSavedAtRef.current
      && pendingLocalSaveRevisionRef.current > consumedLocalSaveRevisionRef.current;
    if (
      saveHadVisibleEditRef.current
      && candidateVisibleSignature !== currentVisibleHydrationSignature
    ) {
      lastHydratedFinancialAidSignatureRef.current = candidateSignature;
      previousVisibleHydrationSignatureRef.current = currentVisibleHydrationSignature;
      hasUnsavedLocalEditRef.current = true;
      lastObservedSavedAtRef.current = lastSavedAt;
      consumedLocalSaveRevisionRef.current = pendingLocalSaveRevisionRef.current;
      saveHadVisibleEditRef.current = false;
      return;
    }

    if (
      hasVisibleLocalDivergence
      && candidateVisibleSignature !== currentVisibleHydrationSignature
      && !hasOwnSaveConfirmation
    ) {
      lastHydratedFinancialAidSignatureRef.current = candidateSignature;
      return;
    }

    hydrateFromSettingsState(candidateSettingsState, candidateSignature);
  }, [currentVisibleHydrationSignature, isLoading, isSaving, lastSavedAt, saveError, settingsState]);

  const requiredFormFieldCount = useMemo(
    () => FORM_REQUIREMENT_FIELDS.filter((field) =>
      resolveFinancialAidRequirementState(requirementPolicy, field) === 'required'
    ).length,
    [requirementPolicy],
  );

  const statusSummary = useMemo(() => {
    const statusLabel = formatImportStatus((importStatus as Record<string, unknown> | undefined)?.status);
    const lastImportedAt = String((importStatus as Record<string, unknown> | undefined)?.lastImportedAt || '').trim();
    const acceptedRows = Number((importStatus as Record<string, unknown> | undefined)?.acceptedRows ?? 0);
    if (!lastImportedAt) {
      return `${statusLabel} · No completed imports yet.`;
    }
    const formattedDate = new Date(lastImportedAt);
    const displayDate = Number.isNaN(formattedDate.getTime())
      ? lastImportedAt
      : formattedDate.toLocaleString();
    return `${statusLabel} · Last import ${displayDate} (${acceptedRows} accepted row${acceptedRows === 1 ? '' : 's'}).`;
  }, [importStatus]);

  const settingsSaveStatus = useMemo(() => {
    if (saveError) {
      return 'Settings were not saved.';
    }
    if (isSaving) {
      return 'Saving settings...';
    }
    if (!lastSavedAt) {
      return 'Changes save automatically.';
    }
    const savedAt = new Date(lastSavedAt);
    if (Number.isNaN(savedAt.getTime())) {
      return 'Saved.';
    }
    return `Saved at ${savedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}.`;
  }, [isSaving, lastSavedAt, saveError]);

  const persistFinancialAid = async (
    nextCycle = cycle,
    nextBudget = budget,
    nextDocs = docs,
    nextNotifs = notifs,
    nextReminderFrequency = reminderFrequency,
    nextCertificationStatements = certificationStatements,
  ) => {
    try {
      await persistPatch((current) => {
        const updatedFinancialAid = upsertFinancialAidCycle(current.financialAid, {
          schoolYear: nextCycle.schoolYear,
          openDate: nextCycle.openDate,
          onTimeDeadline: nextCycle.onTimeDeadline,
          hardCutoffDeadline: nextCycle.hardCutoffDeadline,
          lateFeeAmount: toNumber(nextCycle.lateFeeAmount),
          submissionRequirement: nextCycle.submissionRequirement,
        }, { setPrimary: true });
        current.financialAid = {
          ...updatedFinancialAid,
          budget: {
            ...current.financialAid.budget,
            annualBudget: toNumber(nextBudget.annualBudget),
            warningThreshold: toNumber(nextBudget.warningThreshold),
            criticalThreshold: toNumber(nextBudget.criticalThreshold),
          },
          requiredDocuments: DOC_DEFINITIONS.map((definition) => ({
            id: definition.id,
            label: definition.label,
            enabled: nextDocs[definition.key],
            ...('optionalUpload' in definition && definition.optionalUpload
              ? { optional: true }
              : {}),
          })),
          certificationStatements: sanitizeFinancialAidCertificationStatements(
            nextCertificationStatements,
          ),
          requirementPolicy: sanitizeRequirementPolicy(current.financialAid.requirementPolicy),
          notifications: {
            parent: {
              applicationSubmitted: nextNotifs.appSubmitted,
              statusChange: nextNotifs.statusChange,
              moreInfoNeeded: nextNotifs.infoNeeded,
              decisionReady: nextNotifs.decisionReady,
              appealReceived: nextNotifs.appealReceived,
            },
            staff: {
              newApplication: nextNotifs.newApp,
              resubmission: nextNotifs.resubmission,
              appealSubmitted: nextNotifs.appealSubmitted,
              overdueReminder: nextNotifs.overdue,
            },
            overdueReminderFrequency: nextReminderFrequency,
          },
        };
        return current;
      });
    } catch {
      // The hook restores the previous state and exposes a staff-facing error.
    }
  };

  const persistRequirementPolicy = async (nextRequirementPolicy: FinancialAidRequirementPolicy) => {
    try {
      await persistPatch((current) => {
        current.financialAid = {
          ...current.financialAid,
          requirementPolicy: sanitizeRequirementPolicy(nextRequirementPolicy),
        };
        return current;
      });
    } catch {
      // The hook restores the previous state and exposes a staff-facing error.
    }
  };

  const uploadHistoricalCsv = async (file: File) => {
    setImportPhase({ phase: 'idle' });
    if (!isCsv(file)) {
      setImportPhase({ phase: 'failed', error: 'Historical import requires a .csv file.' });
      return;
    }
    if (file.size <= 0) {
      setImportPhase({ phase: 'failed', error: 'Selected CSV file is empty.' });
      return;
    }
    if (file.size > MAX_IMPORT_SIZE_BYTES) {
      setImportPhase({ phase: 'failed', error: 'CSV file is too large (max 25 MB).' });
      return;
    }

    uploadInFlightRef.current = true;
    setImportPhase({ phase: 'uploading' });
    try {
      const fileType = resolveCsvMimeType(file);
      const uploadInfo = await requestImportUpload({
        orgId: SETTINGS_DEFAULT_ORG_ID,
        moduleKey: SETTINGS_MODULE_KEY,
        fileName: file.name,
        fileType,
        fileSizeBytes: file.size,
      }).unwrap();

      const uploadUrl = String((uploadInfo as Record<string, unknown>).uploadUrl || '').trim();
      const importId = String((uploadInfo as Record<string, unknown>).importId || '').trim();
      if (!uploadUrl || !importId) {
        throw new Error('Upload URL could not be created.');
      }

      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': fileType },
        body: file,
      });
      if (!uploadResponse.ok) {
        throw new Error(`Upload failed (${uploadResponse.status}).`);
      }

      setImportPhase({ phase: 'processing', importId });
      const importResult = await startHistoricalImport({
        orgId: SETTINGS_DEFAULT_ORG_ID,
        moduleKey: SETTINGS_MODULE_KEY,
        importId,
      }).unwrap();
      const initialSnapshot = toImportStatusSnapshot(importResult);
      const terminalSnapshot = TERMINAL_IMPORT_STATUSES.has(initialSnapshot.status)
        ? initialSnapshot
        : await pollImportUntilTerminal();
      if (terminalSnapshot.status === 'failed') {
        throw new Error(terminalSnapshot.errorSummary || 'Historical import failed.');
      }
      const accepted = Number.isFinite(terminalSnapshot.acceptedRows) ? terminalSnapshot.acceptedRows : 0;
      const rejected = Number.isFinite(terminalSnapshot.rejectedRows) ? terminalSnapshot.rejectedRows : 0;
      if (rejected > 0) {
        setImportPhase({ phase: 'partial-success', accepted, rejected, firstErrors: terminalSnapshot.firstErrors });
      } else {
        setImportPhase({ phase: 'complete', accepted, rejected });
      }
      await refetchImportStatus();
    } catch (error) {
      setImportPhase({
        phase: 'failed',
        error: readErrorMessage(error, 'Unable to import historical CSV. Please try again.'),
      });
    } finally {
      uploadInFlightRef.current = false;
    }
  };

  const isImportInFlight =
    importPhase.phase === 'uploading' || importPhase.phase === 'processing';

  const downloadTemplate = async () => {
    if (!isImportInFlight) {
      setImportPhase({ phase: 'idle' });
    }
    setIsDownloadingTemplate(true);
    try {
      const template = await fetchTemplate({
        orgId: SETTINGS_DEFAULT_ORG_ID,
        moduleKey: SETTINGS_MODULE_KEY,
      }).unwrap();
      const downloadUrl = String((template as Record<string, unknown>).downloadUrl || '').trim();
      if (!downloadUrl) {
        throw new Error('Template URL is unavailable.');
      }
      window.open(downloadUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      setImportPhase({
        phase: 'failed',
        error: readErrorMessage(error, 'Unable to download template.'),
      });
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  return isLoading ? (
    <div
      className="rounded-lg border border-border bg-white p-6 text-sm font-medium text-text-secondary shadow-sm"
      data-testid="settings-financial-aid-loading"
    >
      Loading Financial Aid settings...
    </div>
  ) : (
    <div className="space-y-10">
      <div className="flex items-center justify-end">
        <p
          className="text-xs font-medium text-text-secondary"
          data-testid="settings-financial-aid-save-status"
          aria-live="polite"
        >
          {settingsSaveStatus}
        </p>
      </div>

      {saveError ? (
        <div
          className="rounded-lg border border-coral/25 bg-warning-background px-4 py-3"
          role="alert"
          data-testid="settings-financial-aid-save-error"
        >
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <p className="text-sm font-medium text-irongrey">
              {saveError.userMessage}
            </p>
            <Button type="button" variant="ghost" onClick={clearSaveError} className="self-start">
              Dismiss
            </Button>
          </div>
          <details className="mt-2">
            <summary className="cursor-pointer select-none text-xs font-medium text-text-secondary hover:text-irongrey">
              Technical details
            </summary>
            <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap rounded-md bg-white/80 p-3 text-xs text-irongrey">
              {saveError.technicalMessage}
            </pre>
          </details>
        </div>
      ) : null}

      <Card data-testid="settings-financial-aid-cycle-section">
        <SectionHeader title="Cycle Configuration" description="Set dates and deadlines for the financial aid application cycle." />
        <div className="space-y-6">
          {/* 1 — Academic Year */}
          <div>
            <label className="mb-1 block text-sm font-medium text-irongrey/75">School Year</label>
            <CustomSelect
              testId="settings-financial-aid-school-year"
              options={cycleOptions}
              value={cycle.schoolYear || cycleOptions[0]?.value || ''}
              onChange={(value) => {
                selectedCycleYearRef.current = value;
                const existingCycle = getFinancialAidCycleForYear(settingsState.financialAid, value);
                const next = toCycleFormState(existingCycle, value);
                setCycle(next);
                void persistFinancialAid(next, budget, docs, notifs, reminderFrequency);
              }}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-irongrey/75">Submission Requirement</label>
            <CustomSelect
              testId="settings-financial-aid-submission-requirement"
              options={FINANCIAL_AID_SUBMISSION_REQUIREMENT_OPTIONS}
              value={cycle.submissionRequirement}
              onChange={(value) => {
                const submissionRequirement = value as FinancialAidSubmissionRequirement;
                const next = { ...cycle, submissionRequirement };
                setCycle(next);
                void persistFinancialAid(next, budget, docs, notifs, reminderFrequency);
              }}
            />
            <p className="mt-1 text-xs text-text-secondary">
              Applies only to {cycle.schoolYear || 'this financial aid cycle'}. This does not change the registration fee amount; update that in Admissions &gt; Tuition &amp; Fees.
            </p>
          </div>

          {/* 2 — Application Window: Open → arrow → Close */}
          <div>
            <p className="mb-2 text-xs font-semibold text-text-secondary uppercase tracking-wider">
              Application Window
            </p>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] items-end gap-3">
              <Input
                label="Application Open Date"
                value={cycle.openDate}
                onChange={(event) => setCycle((previous) => ({ ...previous, openDate: event.target.value }))}
                onBlur={() => void persistFinancialAid()}
              />
              <div className="hidden md:flex items-center justify-center pb-2 text-text-tertiary">
                <Icon name="ArrowRight" className="w-4 h-4" />
              </div>
              <Input
                label="On-Time Deadline"
                value={cycle.onTimeDeadline}
                onChange={(event) => setCycle((previous) => ({ ...previous, onTimeDeadline: event.target.value }))}
                onBlur={() => void persistFinancialAid()}
                subLabel="Late fee applies after this date."
              />
            </div>
          </div>

          {/* 3 — Enrollment Deadline (hard cutoff) */}
          <Input
            label="Hard Cutoff Date"
            value={cycle.hardCutoffDeadline}
            onChange={(event) => setCycle((previous) => ({ ...previous, hardCutoffDeadline: event.target.value }))}
            onBlur={() => void persistFinancialAid()}
            subLabel="No submissions accepted after this date."
          />

          {/* 4 — Late Fee */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="relative">
              <label className="mb-1 block text-sm font-medium text-irongrey/75">Late Fee Amount</label>
              <div className="relative">
                <span className="absolute left-3 top-2.5 text-irongrey/55 text-sm">$</span>
                <input
                  className="block w-full rounded-md border-border shadow-sm focus:border-primary focus:ring-primary sm:text-sm h-10 pl-7 pr-3 border"
                  value={cycle.lateFeeAmount}
                  onChange={(event) => setCycle((previous) => ({ ...previous, lateFeeAmount: event.target.value }))}
                  onBlur={() => void persistFinancialAid()}
                />
              </div>
            </div>
            <div className="hidden md:block" aria-hidden="true" />
          </div>
        </div>
      </Card>

      <Card data-testid="settings-financial-aid-budget-section">
        <SectionHeader title="Budget" description="Define scholarship budget and alert thresholds." />
        <div className="space-y-6">
          <div className="relative">
            <label className="mb-1 block text-sm font-medium text-irongrey/75">Annual Scholarship Budget</label>
            <div className="relative">
              <span className="absolute left-3 top-2.5 text-irongrey/55 text-sm">$</span>
              <input
                className="block w-full rounded-md border-border shadow-sm focus:border-primary focus:ring-primary sm:text-sm h-10 pl-7 pr-3 border"
                value={budget.annualBudget}
                onChange={(event) => setBudget((previous) => ({ ...previous, annualBudget: event.target.value }))}
                onBlur={() => void persistFinancialAid()}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Warning Threshold (%)"
              type="number"
              value={budget.warningThreshold}
              onChange={(event) => setBudget((previous) => ({ ...previous, warningThreshold: event.target.value }))}
              onBlur={() => void persistFinancialAid()}
              subLabel="Displays yellow indicator on dashboard when reached."
            />
            <Input
              label="Critical Threshold (%)"
              type="number"
              value={budget.criticalThreshold}
              onChange={(event) => setBudget((previous) => ({ ...previous, criticalThreshold: event.target.value }))}
              onBlur={() => void persistFinancialAid()}
              subLabel="Displays red indicator on dashboard when reached."
            />
          </div>
        </div>
      </Card>

      <Card data-testid="settings-financial-aid-docs-section">
        <SectionHeader title="Required Documents" description="Configure what documents families must upload." />
        <div className="mb-4 flex items-center justify-between border-b border-border pb-2 text-xs uppercase tracking-wide text-irongrey/55">
          <span>Document Type</span>
          <span>{Object.values(docs).filter(Boolean).length} of {DOC_DEFINITIONS.length} required</span>
        </div>
        <div className="divide-y divide-border">
          {DOC_DEFINITIONS.map((definition) => (
            <Toggle
              key={definition.id}
              label={definition.label}
              checked={docs[definition.key]}
              onChange={() => {
                const nextDocs = { ...docs, [definition.key]: !docs[definition.key] };
                setDocs(nextDocs);
                void persistFinancialAid(cycle, budget, nextDocs, notifs, reminderFrequency);
              }}
              requiredText={false}
            />
          ))}
        </div>
      </Card>

      <Card data-testid="settings-financial-aid-certification-statements-section">
        <SectionHeader
          title="Certification Statements"
          description="Wording parents confirm when signing the application. Leave a statement blank to use the standard wording."
        />
        <div className="space-y-4">
          {PARENT_FINANCIAL_AID_ATTESTATION_KEYS.map((attestationKey, index) => (
            <div key={attestationKey}>
              <label
                className="mb-1 block text-xs font-semibold uppercase tracking-wide text-irongrey/55"
                htmlFor={`settings-financial-aid-certification-${attestationKey}`}
              >
                Statement {index + 1}
              </label>
              <textarea
                id={`settings-financial-aid-certification-${attestationKey}`}
                data-testid={`settings-financial-aid-certification-${attestationKey}`}
                className="w-full rounded-xl border border-border bg-white px-4 py-2.5 text-sm"
                rows={attestationKey === 'incomeChangeAgreement' ? 5 : 2}
                placeholder={PARENT_FINANCIAL_AID_DEFAULT_ATTESTATION_TEXT[attestationKey]}
                value={certificationStatements[attestationKey] ?? ''}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  setCertificationStatements((previous) => ({
                    ...previous,
                    [attestationKey]: nextValue,
                  }));
                }}
                onBlur={(event) => {
                  const nextStatements = sanitizeFinancialAidCertificationStatements({
                    ...certificationStatements,
                    [attestationKey]: event.target.value,
                  });
                  setCertificationStatements(nextStatements);
                  void persistFinancialAid(
                    cycle,
                    budget,
                    docs,
                    notifs,
                    reminderFrequency,
                    nextStatements,
                  );
                }}
              />
            </div>
          ))}
        </div>
      </Card>

      <Card data-testid="settings-financial-aid-form-requirements-section">
        <SectionHeader title="Form Requirements" description="Set application fields families must complete before submission." />
        <div className="mb-4 flex items-center justify-between border-b border-border pb-2 text-xs uppercase tracking-wide text-irongrey/55">
          <span>Application Field</span>
          <span>{requiredFormFieldCount} of {FORM_REQUIREMENT_FIELDS.length} required</span>
        </div>
        <div className="space-y-6">
          {FORM_REQUIREMENT_FIELD_GROUPS.map((group) => (
            <div key={group.group}>
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-irongrey">
                {group.group}
              </h4>
              <div className="divide-y divide-border">
                {group.fields.map((field) => {
                  const mode = resolveFinancialAidRequirementState(requirementPolicy, field);
                  return (
                    <div
                      key={field.fieldKey}
                      className="grid grid-cols-1 gap-3 py-3 md:grid-cols-[minmax(0,1fr)_180px] md:items-center"
                    >
                      <div className="min-w-0">
                        <span className="text-sm font-medium text-irongrey">{field.label}</span>
                      </div>
                      <CustomSelect
                        testId={toRequirementModeTestId(field.fieldKey)}
                        options={[...FORM_REQUIREMENT_MODE_OPTIONS]}
                        value={mode}
                        onChange={(value) => {
                          const nextPolicy = updateRequirementPolicy(
                            requirementPolicy,
                            field.fieldKey,
                            value as FinancialAidRequirementState,
                          );
                          setRequirementPolicy(nextPolicy);
                          void persistRequirementPolicy(nextPolicy);
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card data-testid="settings-financial-aid-notifications-section">
        <SectionHeader title="Notifications" description="Manage automated emails for families and staff." />
        <div className="space-y-8">
          <div>
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-irongrey">Parent Notifications</h4>
            <div className="divide-y divide-border">
              <Toggle label="Application Submitted" checked={notifs.appSubmitted} onChange={() => {
                const nextNotifs = { ...notifs, appSubmitted: !notifs.appSubmitted };
                setNotifs(nextNotifs);
                void persistFinancialAid(cycle, budget, docs, nextNotifs, reminderFrequency);
              }} requiredText={false} />
              <Toggle label="Status Change" checked={notifs.statusChange} onChange={() => {
                const nextNotifs = { ...notifs, statusChange: !notifs.statusChange };
                setNotifs(nextNotifs);
                void persistFinancialAid(cycle, budget, docs, nextNotifs, reminderFrequency);
              }} requiredText={false} />
              <Toggle label="More Information Needed" checked={notifs.infoNeeded} onChange={() => {
                const nextNotifs = { ...notifs, infoNeeded: !notifs.infoNeeded };
                setNotifs(nextNotifs);
                void persistFinancialAid(cycle, budget, docs, nextNotifs, reminderFrequency);
              }} requiredText={false} />
              <Toggle label="Decision Ready" checked={notifs.decisionReady} onChange={() => {
                const nextNotifs = { ...notifs, decisionReady: !notifs.decisionReady };
                setNotifs(nextNotifs);
                void persistFinancialAid(cycle, budget, docs, nextNotifs, reminderFrequency);
              }} requiredText={false} />
              <Toggle label="Appeal Received" checked={notifs.appealReceived} onChange={() => {
                const nextNotifs = { ...notifs, appealReceived: !notifs.appealReceived };
                setNotifs(nextNotifs);
                void persistFinancialAid(cycle, budget, docs, nextNotifs, reminderFrequency);
              }} requiredText={false} />
            </div>
          </div>

          <div>
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-irongrey">Staff Notifications</h4>
            <div className="divide-y divide-border">
              <Toggle label="New Application Received" checked={notifs.newApp} onChange={() => {
                const nextNotifs = { ...notifs, newApp: !notifs.newApp };
                setNotifs(nextNotifs);
                void persistFinancialAid(cycle, budget, docs, nextNotifs, reminderFrequency);
              }} requiredText={false} />
              <Toggle label="Resubmission / Info Added" checked={notifs.resubmission} onChange={() => {
                const nextNotifs = { ...notifs, resubmission: !notifs.resubmission };
                setNotifs(nextNotifs);
                void persistFinancialAid(cycle, budget, docs, nextNotifs, reminderFrequency);
              }} requiredText={false} />
              <Toggle label="Appeal Submitted" checked={notifs.appealSubmitted} onChange={() => {
                const nextNotifs = { ...notifs, appealSubmitted: !notifs.appealSubmitted };
                setNotifs(nextNotifs);
                void persistFinancialAid(cycle, budget, docs, nextNotifs, reminderFrequency);
              }} requiredText={false} />
              <Toggle label="Processing Overdue Reminder" checked={notifs.overdue} onChange={() => {
                const nextNotifs = { ...notifs, overdue: !notifs.overdue };
                setNotifs(nextNotifs);
                void persistFinancialAid(cycle, budget, docs, nextNotifs, reminderFrequency);
              }} requiredText={false} />
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <div className="w-40">
              <CustomSelect
                testId="settings-financial-aid-reminder-frequency"
                options={[
                  { label: 'Daily', value: 'daily' },
                  { label: 'Every 2 Days', value: 'every_2_days' },
                  { label: 'Weekly', value: 'weekly' },
                ]}
                value={reminderFrequency}
                onChange={(value) => {
                  const nextFrequency = value as 'daily' | 'every_2_days' | 'weekly';
                  setReminderFrequency(nextFrequency);
                  void persistFinancialAid(cycle, budget, docs, notifs, nextFrequency);
                }}
              />
            </div>
          </div>
        </div>
      </Card>

      <SectionDivider label="Data Operations" className="my-4" />

      <Card>
        <SectionHeader title="Historical Data Import" description="Import past financial data for the algorithm comparison logic." />
        <div className="mb-6 flex flex-col justify-between gap-4 rounded-lg bg-muted/70 p-4 md:flex-row md:items-center">
          <div>
            <h4 className="text-sm font-medium text-irongrey">Data Template</h4>
            <p className="text-xs text-irongrey/55">Download the required CSV format.</p>
          </div>
          <Button
            variant="outline"
            onClick={() => void downloadTemplate()}
            disabled={isDownloadingTemplate}
            data-testid="settings-financial-aid-download-template"
          >
            <Icon name="Download" className="w-4 h-4 mr-2" />
            {isDownloadingTemplate ? 'Downloading...' : 'Download Template'}
          </Button>
        </div>
        <DropzoneField
          accept=".csv,text/csv"
          maxSizeBytes={MAX_IMPORT_SIZE_BYTES}
          onFile={(file) => void uploadHistoricalCsv(file)}
          label="Click to upload or drag and drop"
          testId="settings-financial-aid-import-dropzone"
          inputTestId="settings-financial-aid-upload-input"
          disabled={isImportInFlight}
        />

        {importPhase.phase === 'processing' && (
          <div className="mt-4" aria-live="polite">
            <p className="text-xs text-text-secondary mb-1.5 flex items-center gap-1.5">
              <Icon name="Loader2" className="w-3.5 h-3.5 animate-spin" />
              Processing import…
            </p>
            <progress
              className="w-full h-1.5 rounded-full"
              aria-label="Import processing in progress"
            />
          </div>
        )}

        <p className="mt-3 text-xs text-text-tertiary" data-testid="settings-financial-aid-import-status">
          {statusSummary}
          {importPhase.phase === 'uploading' ? ' · Uploading…' : ''}
          {importPhase.phase === 'processing' ? ' · Processing…' : ''}
        </p>

        {importPhase.phase === 'complete' ? (
          <p className="mt-2 text-xs text-jungle" data-testid="settings-financial-aid-import-message">
            Import completed. {importPhase.accepted} accepted row{importPhase.accepted === 1 ? '' : 's'}.
          </p>
        ) : null}

        {importPhase.phase === 'partial-success' ? (
          <div
            className="mt-3 rounded-md bg-warning-background border border-coral/20 px-4 py-3"
            data-testid="settings-financial-aid-import-message"
            role="alert"
          >
            <EngineeringSupportBanner compact className="mb-3" />
            <p className="text-xs font-semibold text-irongrey">
              Partially imported: {importPhase.accepted} accepted{' '}
              {importPhase.accepted === 1 ? 'row' : 'rows'},{' '}
              {importPhase.rejected} rejected.
            </p>
            {importPhase.firstErrors.length > 0 && (
              <details className="mt-2">
                <summary className="text-xs text-text-secondary cursor-pointer hover:text-irongrey select-none">
                  Show {importPhase.firstErrors.length} error{importPhase.firstErrors.length === 1 ? '' : 's'}
                </summary>
                <ul className="mt-2 space-y-1 pl-2">
                  {importPhase.firstErrors.map((err, idx) => (
                    <li key={idx} className="text-xs text-strawberry font-mono">
                      {err}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        ) : null}

        {importPhase.phase === 'failed' ? (
          <SupportErrorMessage className="mt-3 text-xs" data-testid="settings-financial-aid-import-error">
            {importPhase.error}
          </SupportErrorMessage>
        ) : null}
      </Card>
    </div>
  );
}
